// Lógica pura de la bandeja de consultas del proyecto (ADR-0145, #0738):
// orden, filtro por estado, conteos, texto del plazo, estado en la URL,
// paginación y los «N nuevos» del hilo. Sin React, para poder testearla
// sola. La pantalla (routes/ConsultasDelProyectoRoute.tsx) y el drawer
// (components/ConsultaDrawer.tsx) sólo pintan lo que sale de acá.
//
// El ESTADO de cada consulta lo deriva el servidor (`estado` en el wire,
// `ownerInquiryEstado` de api-types) — acá no se recalcula ni se compara
// `dueAt` contra el reloj para decidirlo: sólo se escribe el texto del
// plazo a partir del estado ya resuelto. Las etiquetas visibles salen de
// `OWNER_INQUIRY_ESTADO_LABEL`, nunca como literal (fitness
// postventa-labels-single-source).

import {
  OWNER_INQUIRY_ESTADO_LABEL,
  OWNER_INQUIRY_THREAD_POLL_MS,
  ownerInquiryEstadoSchema,
  type OwnerInquiryEntry,
  type OwnerInquiryEstado,
  type OwnerInquiryListItem,
} from "@bitacorapro/api-types";
import { ApiError } from "../api/error";
import {
  clampPage,
  diasCalendario,
  fechaCorta,
  PAGE_SIZES,
  pageSlice,
  totalPages,
  type PageSize,
  type PlazoTone,
} from "./observacionesList";

export { clampPage, PAGE_SIZES, pageSlice, totalPages, type PageSize };

export const ESTADOS: readonly OwnerInquiryEstado[] = ownerInquiryEstadoSchema.options;

/** Tono de la pill de estado, por estado. El mapa de tono → clase CSS
 *  vive en la pantalla (pills.ts tiene el de observaciones). */
export const ESTADO_TONE: Record<OwnerInquiryEstado, PlazoTone> = {
  sin_responder: "warn",
  vencida: "crit",
  respondida: "ok",
};

export function estadoLabel(estado: OwnerInquiryEstado): string {
  return OWNER_INQUIRY_ESTADO_LABEL[estado];
}

// ── Orden y filtro ───────────────────────────────────────────────────

/** Prioridad de la fila: lo que el jefe todavía no abrió primero, después
 *  lo vencido, después el resto; dentro de cada grupo la de actividad más
 *  reciente arriba (`lastEntryAt`: un mensaje nuevo del propietario la
 *  sube, ADR-0155) y, a igualdad, la más nueva. */
function prioridad(c: OwnerInquiryListItem): number {
  if (c.unreadCount > 0) return 0;
  if (c.estado === "vencida") return 1;
  return 2;
}

export function ordenarConsultas(items: readonly OwnerInquiryListItem[]): OwnerInquiryListItem[] {
  return [...items].sort((a, b) => {
    const p = prioridad(a) - prioridad(b);
    if (p !== 0) return p;
    const actividad = b.lastEntryAt.localeCompare(a.lastEntryAt);
    if (actividad !== 0) return actividad;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export function filtrarPorEstado(
  items: readonly OwnerInquiryListItem[],
  estado: OwnerInquiryEstado | null
): OwnerInquiryListItem[] {
  if (estado === null) return [...items];
  return items.filter((c) => c.estado === estado);
}

export type ConteosConsultas = Record<OwnerInquiryEstado, number> & { total: number };

export function contarPorEstado(items: readonly OwnerInquiryListItem[]): ConteosConsultas {
  const out: ConteosConsultas = {
    sin_responder: 0,
    vencida: 0,
    respondida: 0,
    total: items.length,
  };
  for (const c of items) out[c.estado] += 1;
  return out;
}

/** ADR-0155 — el propietario volvió a escribir en una consulta que ya
 *  tenía respuesta: espera respuesta (no está respondida) pero hubo una
 *  primera. Distingue «Nuevo mensaje del propietario» de una consulta
 *  nueva. */
export function esReabierta(c: Pick<OwnerInquiryListItem, "estado" | "firstResponseAt">): boolean {
  return c.estado !== "respondida" && c.firstResponseAt !== null;
}

// ── Plazo ────────────────────────────────────────────────────────────

export type PlazoRespuesta = { texto: string; tone: PlazoTone };

function dias(n: number): string {
  return `${n} ${n === 1 ? "día" : "días"}`;
}

/** El texto de la columna Plazo, a partir del estado que ya derivó el
 *  servidor (días corridos a medianoche de Chile, como el plazo del
 *  contratista):
 *  - respondida → «Respondida {fecha de la última respuesta}»
 *    (`answeredAt`, ADR-0155; `firstResponseAt` como respaldo);
 *  - vencida → «Vencida hace N días» (o «Vencida hoy» si venció hace unas
 *    horas: el servidor vence al pasar `dueAt`, que cae a media mañana);
 *  - sin responder → «Vence hoy» / «Vence en N días». */
export function plazoRespuesta(
  c: Pick<OwnerInquiryListItem, "estado" | "dueAt" | "firstResponseAt"> & {
    answeredAt?: string | null;
  },
  now: Date
): PlazoRespuesta {
  if (c.estado === "respondida") {
    const cuando = c.answeredAt ?? c.firstResponseAt;
    return {
      texto: cuando
        ? `${estadoLabel("respondida")} ${fechaCorta(cuando)}`
        : estadoLabel("respondida"),
      tone: "ok",
    };
  }
  const restantes = diasCalendario(now, new Date(c.dueAt));
  if (c.estado === "vencida") {
    return {
      texto:
        restantes < 0
          ? `${estadoLabel("vencida")} hace ${dias(-restantes)}`
          : `${estadoLabel("vencida")} hoy`,
      tone: "crit",
    };
  }
  if (restantes <= 0) return { texto: "Vence hoy", tone: "warn" };
  return { texto: `Vence en ${dias(restantes)}`, tone: restantes <= 2 ? "warn" : "info" };
}

// ── Estado en la URL ─────────────────────────────────────────────────

export type ConsultasListState = {
  estado: OwnerInquiryEstado | null;
  pagina: number;
  tam: PageSize;
  /** `?consulta=<id>`: el hilo abierto en el drawer (el deep link del
   *  correo al jefe). */
  consulta: string | null;
};

export const PAGE_SIZE_DEFAULT: PageSize = 10;

export function parseEstado(raw: string | null): OwnerInquiryEstado | null {
  const r = ownerInquiryEstadoSchema.safeParse(raw);
  return r.success ? r.data : null;
}

export function parseConsultasState(sp: URLSearchParams): ConsultasListState {
  const pagina = Number.parseInt(sp.get("pagina") ?? "", 10);
  const tam = Number.parseInt(sp.get("tam") ?? "", 10);
  return {
    estado: parseEstado(sp.get("estado")),
    pagina: Number.isFinite(pagina) && pagina >= 1 ? pagina : 1,
    tam: (PAGE_SIZES as readonly number[]).includes(tam) ? (tam as PageSize) : PAGE_SIZE_DEFAULT,
    consulta: sp.get("consulta") || null,
  };
}

export type ConsultasPatch = Partial<{
  estado: OwnerInquiryEstado | null;
  pagina: number;
  tam: PageSize;
  consulta: string | null;
}>;

/** Escribe el patch sobre la URL; cada clave se borra en su default.
 *  Cambiar el filtro o el tamaño vuelve a la página 1. */
export function writeConsultasState(prev: URLSearchParams, patch: ConsultasPatch): URLSearchParams {
  const next = new URLSearchParams(prev);
  if ("estado" in patch) {
    if (patch.estado) next.set("estado", patch.estado);
    else next.delete("estado");
    next.delete("pagina");
  }
  if ("tam" in patch && patch.tam !== undefined) {
    if (patch.tam === PAGE_SIZE_DEFAULT) next.delete("tam");
    else next.set("tam", String(patch.tam));
    next.delete("pagina");
  }
  if ("pagina" in patch && patch.pagina !== undefined) {
    if (patch.pagina <= 1) next.delete("pagina");
    else next.set("pagina", String(patch.pagina));
  }
  if ("consulta" in patch) {
    if (patch.consulta) next.set("consulta", patch.consulta);
    else next.delete("consulta");
  }
  return next;
}

// ── Hilo ─────────────────────────────────────────────────────────────

/** El `createdAt` de la última entrada mostrada: lo que se manda a
 *  POST …/read. Nunca el reloj del cliente. */
export function ultimaEntradaAt(entries: readonly OwnerInquiryEntry[]): string | null {
  let max: string | null = null;
  for (const e of entries) if (max === null || e.createdAt > max) max = e.createdAt;
  return max;
}

/** Índice de la primera entrada «nueva» para quien lee (posterior a su
 *  marca previa; todas si nunca abrió el hilo), sin contar lo que escribió
 *  él mismo, y cuántas hay desde ahí. -1 / 0 si no hay nada nuevo. */
export function primeraNueva(
  entries: readonly OwnerInquiryEntry[],
  lastReadAt: string | null,
  selfUserId: string | null
): { index: number; nuevos: number } {
  let index = -1;
  let nuevos = 0;
  entries.forEach((e, i) => {
    const propia = e.author.kind === "user" && e.author.userId === selfUserId;
    const posterior = lastReadAt === null || e.createdAt > lastReadAt;
    if (!propia && posterior) {
      if (index === -1) index = i;
      nuevos += 1;
    }
  });
  return { index, nuevos };
}

export function nuevosLabel(n: number): string {
  return `${n} ${n === 1 ? "nuevo" : "nuevos"}`;
}

const NUMERO_CL = new Intl.NumberFormat("es-CL");

/** «N de 2.000 caracteres» — el contador del textarea de respuesta. */
export function contadorCaracteres(largo: number, max: number): string {
  return `${NUMERO_CL.format(largo)} de ${NUMERO_CL.format(max)} caracteres`;
}

// ── ADR-0158: casi en vivo y adjuntos ───────────────────────────────

/** Cada cuánto se revisa el hilo abierto: nunca tras un 403/404 (la
 *  consulta ya no es de quien mira), sí tras cualquier otro error. */
export function intervaloDelHilo(error: unknown): number | false {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return false;
  return OWNER_INQUIRY_THREAD_POLL_MS;
}

/** «1 foto · 1 PDF», «2 fotos». */
export function resumenAdjuntos(list: readonly { tipo: "foto" | "pdf" }[]): string {
  const pdfs = list.filter((a) => a.tipo === "pdf").length;
  const fotos = list.length - pdfs;
  return [
    fotos > 0 ? `${fotos} ${fotos === 1 ? "foto" : "fotos"}` : null,
    pdfs > 0 ? `${pdfs} PDF` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(" · ");
}

/** «1 mensaje nuevo del propietario» / «2 mensajes nuevos …» — el aviso
 *  al lector de pantalla cuando la revisión trae algo. */
export function avisoMensajesNuevos(n: number): string {
  return n === 1 ? "1 mensaje nuevo del propietario" : `${n} mensajes nuevos del propietario`;
}

/** El texto de la región live. Dos avisos iguales seguidos («1 mensaje
 *  nuevo…» y otro «1 mensaje nuevo…») dejarían el DOM igual y el lector
 *  de pantalla no leería el segundo: cada vuelta impar suma un espacio
 *  duro, que no se oye pero cambia el texto. */
export function textoAnuncio(n: number, vuelta: number): string {
  return avisoMensajesNuevos(n) + (vuelta % 2 === 1 ? " " : "");
}

/** Las URL de lectura de un adjunto duran 120 s. Antes de abrir el visor
 *  se re-firman si tienen más de 60 s: el visor monta un <img> nuevo y,
 *  con `no-store`, el navegador vuelve a pedir la URL. */
export const URL_ADJUNTO_SEGURA_MS = 60_000;

export function urlsPorVencer(firmadasEn: number, ahora: number): boolean {
  return firmadasEn <= 0 || ahora - firmadasEn > URL_ADJUNTO_SEGURA_MS;
}

/** ¿El servidor rechazó una CLAVE de archivo ya subido (ya registrada en
 *  otro mensaje, o el objeto no está)? Entonces reutilizarla no sirve: el
 *  próximo envío sube el archivo de nuevo. Pasa cuando un envío con PDF se
 *  guardó pero su respuesta se perdió y después se editó el texto. */
export function claveDeArchivoInservible(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 409 && err.code === "ATTACHMENT_ALREADY_REGISTERED") return true;
  return err.status === 400 && typeof err.code === "string" && err.code.startsWith("OBJECT_");
}
